import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import {
  ensureThemeJsonArray,
  getDvSourceImageUrl,
  getLandingPageTitleColor,
} from "../../functions/themeAssets";
import {
  STAGE_ROW_KEY,
  bootstrapStageAndReport,
  clearStageActive,
  markStageCompleted,
  redirectIfStageAlreadyPlayed,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import { showStageCompleteCelebration } from "../../functions/stageCelebration";
import StageCorrectPopup from "./StageCorrectPopup";
import {
  fireStageHelpSwal,
  getStageRulesFromTheme,
} from "../../functions/stageRules";
import Swal from "sweetalert2";
import {
  generateLayout,
  getWordStartPositions,
} from "../../admin/pages/themeupdate/crosswordLayout";
import "../fonts/breuer-headline.css";
import "./crosswordStage.css";
import "./stageActions.css";

const GRID_GAP = 2;
const DESKTOP_CELL = 32;
const STORAGE_TIME_KEY = "crossword_stage1_timer";

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

/** A hinted/filled space (" ") must count as filled — trim() would treat it as empty. */
function isCrosswordCellFilled(value) {
  return value !== undefined && value !== null && value !== "";
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function normalizePairs(themeData) {
  const raw = ensureThemeJsonArray(themeData?.questions_and_answers);
  return raw
    .map((item) => ({
      question: String(item?.question ?? item?.clue ?? "").trim(),
      answer: String(item?.answer ?? "")
        .trim()
        .toLowerCase(),
    }))
    .filter((p) => p.question && p.answer);
}

function parseSavedLayout(raw) {
  if (!raw) return null;
  let data = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!data?.table?.length || !data?.rows || !data?.cols) return null;
  const words = Array.isArray(data.words)
    ? data.words
    : Object.values(data.words || {});
  return {
    table: data.table,
    rows: data.rows,
    cols: data.cols,
    result: words.reduce((acc, w, i) => {
      acc[i] = w;
      return acc;
    }, {}),
  };
}

function getDirection(row, col, table) {
  if (col + 1 < table[row].length && table[row][col + 1] !== "-") return "across";
  if (row + 1 < table.length && table[row + 1][col] !== "-") return "down";
  return "across";
}

function findWordsAtCell(words, row, col) {
  return words.filter((word) => {
    const row0 = word.starty - 1;
    const col0 = word.startx - 1;
    for (let i = 0; i < word.answer.length; i++) {
      const r = word.orientation === "down" ? row0 + i : row0;
      const c = word.orientation === "across" ? col0 + i : col0;
      if (r === row && c === col) return true;
    }
    return false;
  });
}

function findWordAtCell(words, row, col, preferOrientation) {
  const matches = findWordsAtCell(words, row, col);
  if (!matches.length) return null;
  if (preferOrientation) {
    const preferred = matches.find((w) => w.orientation === preferOrientation);
    if (preferred) return preferred;
  }
  return matches[0];
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function CrosswordStage1() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [stageRowId, setStageRowId] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [points, setPoints] = useState(0);
  const [completedAnswers, setCompletedAnswers] = useState(() => new Set());
  const [activeWordKey, setActiveWordKey] = useState(null);
  const [cellValues, setCellValues] = useState({});
  const [cellStates, setCellStates] = useState({});
  const [showCorrectPopup, setShowCorrectPopup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replayChecked, setReplayChecked] = useState(false);
  const [currentDirection, setCurrentDirection] = useState(null);

  const gridRef = useRef(null);
  const elapsedRef = useRef(0);
  const stageRowIdRef = useRef(null);
  const reportIdRef = useRef(null);
  const completedRef = useRef(new Set());
  const cellValuesRef = useRef({});
  const firstHintUsedRef = useRef(false);
  const currentDirectionRef = useRef(null);
  const activeWordKeyRef = useRef(null);
  const isAutoNavigatingRef = useRef(false);
  const timerStartedRef = useRef(false);
  const submittedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const pointsPerWord = useMemo(() => {
    const p = Number(themeData?.points);
    return Number.isFinite(p) && p > 0 ? p : 10;
  }, [themeData?.points]);

  const useTimeout = useMemo(() => {
    const v = themeData?.use_timeout;
    return v === true || v === "true" || v === 1 || v === "1";
  }, [themeData?.use_timeout]);

  const initialTimerSec = useMemo(() => {
    if (!useTimeout) return 0;
    return parseClockToSeconds(themeData?.timer || "05:00");
  }, [themeData?.timer, useTimeout]);

  const title = useMemo(() => {
    const t =
      themeData?.downandacross_title ||
      themeData?.landing_page_title ||
      themeData?.main_title ||
      themeData?.themename ||
      "Down and Across";
    return String(t).trim();
  }, [themeData]);

  const subtitle = useMemo(() => {
    const custom = themeData?.downandacross_subtitle;
    if (custom && String(custom).trim()) return String(custom).trim();
    return "Complete the crossword by solving the clues given below.";
  }, [themeData]);

  const titleTextColor = useMemo(
    () => getLandingPageTitleColor(themeData),
    [themeData]
  );

  const themedTextStyle = useMemo(
    () => (titleTextColor ? { color: titleTextColor } : undefined),
    [titleTextColor]
  );

  const layoutBundle = useMemo(() => {
    const pairs = normalizePairs(themeData);
    if (!pairs.length) return null;

    const saved = parseSavedLayout(themeData?.crossword_layout);
    if (saved) {
      const words = Object.values(saved.result).filter(
        (w) => w.orientation && w.orientation !== "none"
      );
      return { layout: saved, words, pairs };
    }

    const wordsInput = pairs.map((p) => ({
      answer: p.answer,
      clue: p.question,
      orientation: "none",
    }));
    const layout = generateLayout(wordsInput);
    const words = Object.values(layout.result).filter(
      (w) => w.orientation && w.orientation !== "none"
    );
    return { layout, words, pairs };
  }, [themeData]);

  const wordStartPositions = useMemo(() => {
    if (!layoutBundle?.words) return {};
    return getWordStartPositions(layoutBundle.words);
  }, [layoutBundle?.words]);

  const acrossClues = useMemo(
    () => layoutBundle?.words.filter((w) => w.orientation === "across") || [],
    [layoutBundle?.words]
  );
  const downClues = useMemo(
    () => layoutBundle?.words.filter((w) => w.orientation === "down") || [],
    [layoutBundle?.words]
  );

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    document.body.classList.add("crossword-page");
    return () => {
      document.body.classList.remove("crossword-page");
    };
  }, []);

  useEffect(() => {
    stageRowIdRef.current = stageRowId;
  }, [stageRowId]);

  useEffect(() => {
    completedRef.current = completedAnswers;
  }, [completedAnswers]);

  useEffect(() => {
    cellValuesRef.current = cellValues;
  }, [cellValues]);

  useEffect(() => {
    currentDirectionRef.current = currentDirection;
  }, [currentDirection]);

  useEffect(() => {
    activeWordKeyRef.current = activeWordKey;
  }, [activeWordKey]);

  useEffect(() => {
    if (!backendBase || !adminToken || timerStartedRef.current) return;
    if (themeStatus !== "succeeded" || !layoutBundle) return;

    timerStartedRef.current = true;
    const startMs = Date.now();
    const storedStart = sessionStorage.getItem(STORAGE_TIME_KEY);
    const baseStart = storedStart ? Number(storedStart) : startMs;
    if (!storedStart) sessionStorage.setItem(STORAGE_TIME_KEY, String(baseStart));

    const tick = setInterval(() => {
      const elapsed = Math.floor((Date.now() - baseStart) / 1000);
      if (useTimeout && initialTimerSec > 0) {
        const left = Math.max(0, initialTimerSec - elapsed);
        elapsedRef.current = elapsed;
        setElapsedSec(left);
        if (left <= 0) {
          clearInterval(tick);
          handleSubmit(true);
        }
      } else {
        elapsedRef.current = elapsed;
        setElapsedSec(elapsed);
      }
    }, 1000);

    return () => clearInterval(tick);
  }, [themeStatus, layoutBundle, backendBase, adminToken, useTimeout, initialTimerSec]);

  useEffect(() => {
    if (!backendBase || !adminToken || isDemoBypass) return;

    let cancelled = false;

    bootstrapStageAndReport({
      backendBase,
      adminToken,
      storedUser,
      isDemoBypass,
      reportIdRef,
      onStageRowId: (id) => {
        if (cancelled) return;
        stageRowIdRef.current = id;
        setStageRowId(id);
      },
    });

    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser]);

  useEffect(() => {
    if (isDemoBypass || !backendBase || !adminToken || !stageRowId) {
      if (isDemoBypass || !backendBase || !adminToken) {
        setReplayChecked(true);
      }
      return;
    }

    let cancelled = false;
    redirectIfStageAlreadyPlayed({
      backendBase,
      adminToken,
      storedUser,
      isDemoBypass,
      stageNumber: 1,
      navigate,
    }).then((redirected) => {
      if (!cancelled && !redirected) {
        setStageActive(1, storedUser);
        setReplayChecked(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    stageRowId,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    navigate,
  ]);

  const getTimeString = useCallback(() => {
    if (useTimeout && initialTimerSec > 0) {
      const taken = Math.min(elapsedRef.current, initialTimerSec);
      return formatSecondsToClock(taken);
    }
    return formatSecondsToClock(elapsedRef.current);
  }, [useTimeout, initialTimerSec]);

  const timerDisplay = useMemo(() => {
    if (useTimeout && initialTimerSec > 0) {
      return formatSecondsToClock(elapsedSec);
    }
    return formatSecondsToClock(elapsedSec);
  }, [elapsedSec, useTimeout, initialTimerSec]);

  const focusWord = useCallback((word) => {
    const key = `${word.starty},${word.startx},${word.orientation}`;
    setActiveWordKey(key);
    activeWordKeyRef.current = key;
    setCurrentDirection(word.orientation);
    currentDirectionRef.current = word.orientation;
    const { layout } = layoutBundle;
    const { table } = layout;
    const row0 = word.starty - 1;
    const col0 = word.startx - 1;
    for (let i = 0; i < word.answer.length; i++) {
      const r = word.orientation === "down" ? row0 + i : row0;
      const c = word.orientation === "across" ? col0 + i : col0;
      if (table[r]?.[c] !== "-") {
        const input = document.querySelector(
          `[data-cw-row="${r}"][data-cw-col="${c}"]`
        );
        if (input && !input.disabled) {
          isAutoNavigatingRef.current = true;
          input.focus();
          break;
        }
      }
    }
  }, [layoutBundle]);

  const focusSiblingInWord = useCallback(
    (row, col, step, directionOverride) => {
      if (!layoutBundle) return;

      const orientation =
        directionOverride || currentDirectionRef.current || currentDirection;
      let word = orientation
        ? findWordAtCell(layoutBundle.words, row, col, orientation)
        : null;

      if (!word && activeWordKeyRef.current) {
        word = layoutBundle.words.find(
          (w) =>
            `${w.starty},${w.startx},${w.orientation}` ===
            activeWordKeyRef.current
        );
      }

      if (!word) return;

      const row0 = word.starty - 1;
      const col0 = word.startx - 1;
      let currentIndex = -1;
      for (let i = 0; i < word.answer.length; i++) {
        const r = word.orientation === "down" ? row0 + i : row0;
        const c = word.orientation === "across" ? col0 + i : col0;
        if (r === row && c === col) {
          currentIndex = i;
          break;
        }
      }
      if (currentIndex === -1) return;

      const delta = step > 0 ? 1 : -1;
      for (
        let i = currentIndex + delta;
        i >= 0 && i < word.answer.length;
        i += delta
      ) {
        const r = word.orientation === "down" ? row0 + i : row0;
        const c = word.orientation === "across" ? col0 + i : col0;
        const input = document.querySelector(
          `[data-cw-row="${r}"][data-cw-col="${c}"]`
        );
        if (input && !input.disabled) {
          isAutoNavigatingRef.current = true;
          input.focus();
          return;
        }
      }
    },
    [layoutBundle, currentDirection]
  );

  const checkWordCompletion = useCallback(
    (words, values) => {
      if (!layoutBundle) return;
      const newlyDone = [];
      for (const word of words) {
        const { answer, orientation, startx, starty } = word;
        if (completedRef.current.has(answer)) continue;
        let done = true;
        for (let i = 0; i < answer.length; i++) {
          const r = orientation === "down" ? starty - 1 + i : starty - 1;
          const c = orientation === "across" ? startx - 1 + i : startx - 1;
          const key = `${r},${c}`;
          const val = (values[key] || "").toUpperCase();
          if (val !== answer[i].toUpperCase()) {
            done = false;
            break;
          }
        }
        if (done) newlyDone.push(answer);
      }
      if (!newlyDone.length) return;

      const isFirstWordCompletion = completedRef.current.size === 0;

      setCompletedAnswers((prev) => {
        const next = new Set(prev);
        newlyDone.forEach((a) => next.add(a));
        return next;
      });
      newlyDone.forEach((a) => completedRef.current.add(a));
      setPoints((p) => p + pointsPerWord * newlyDone.length);
      setShowCorrectPopup(true);
      setTimeout(() => setShowCorrectPopup(false), 1500);
      setCurrentDirection(null);

      if (
        isDemoBypass &&
        isFirstWordCompletion &&
        !demoRedirectTriggeredRef.current
      ) {
        demoRedirectTriggeredRef.current = true;
        setTimeout(() => {
          void Swal.fire({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window.location.assign("https://extramileplay.com/plans");
          });
        }, 400);
      }
    },
    [layoutBundle, pointsPerWord, isDemoBypass]
  );

  const handleCellInput = useCallback(
    (row, col, letter, dir) => {
      const key = `${row},${col}`;
      const correct = layoutBundle.layout.table[row][col].toUpperCase();
      const val = letter.slice(-1).toUpperCase();

      setCellValues((prev) => {
        const next = { ...prev, [key]: val };
        checkWordCompletion(layoutBundle.words, next);
        return next;
      });

      setCellStates((prev) => {
        const next = { ...prev };
        if (!val) {
          delete next[key];
          return next;
        }
        if (val === correct) {
          next[key] = "correct";
        } else {
          next[key] = "wrong";
        }
        return next;
      });

      const travelDir =
        currentDirectionRef.current || currentDirection || dir;
      if (!currentDirectionRef.current && !currentDirection) {
        setCurrentDirection(travelDir);
        currentDirectionRef.current = travelDir;
      }

      if (val) {
        const activeWord = findWordAtCell(
          layoutBundle.words,
          row,
          col,
          travelDir
        );
        if (activeWord) {
          const wordKey = `${activeWord.starty},${activeWord.startx},${activeWord.orientation}`;
          setActiveWordKey(wordKey);
          activeWordKeyRef.current = wordKey;
        }

        requestAnimationFrame(() => {
          focusSiblingInWord(row, col, 1, travelDir);
        });
      }
    },
    [layoutBundle, checkWordCompletion, currentDirection, focusSiblingInWord]
  );

  const getWordsByClueOrder = useCallback(() => {
    if (!layoutBundle) return [];
    return [...layoutBundle.words]
      .filter((w) => w.orientation && w.orientation !== "none")
      .sort((a, b) => {
        const posA =
          wordStartPositions[`${a.starty},${a.startx}`] ?? a.position ?? 999;
        const posB =
          wordStartPositions[`${b.starty},${b.startx}`] ?? b.position ?? 999;
        return posA - posB;
      });
  }, [layoutBundle, wordStartPositions]);

  const revealLetterInWord = useCallback(
    (word, letterIndex) => {
      const { orientation, startx, starty, answer } = word;
      if (letterIndex < 0 || letterIndex >= answer.length) return false;
      const r = orientation === "down" ? starty - 1 + letterIndex : starty - 1;
      const c = orientation === "across" ? startx - 1 + letterIndex : startx - 1;
      const key = `${r},${c}`;
      if (isCrosswordCellFilled(cellValuesRef.current[key])) return false;

      const letter = answer[letterIndex].toUpperCase();
      setCellValues((prev) => {
        const next = { ...prev, [key]: letter };
        checkWordCompletion(layoutBundle.words, next);
        return next;
      });
      setCellStates((prev) => ({ ...prev, [key]: "correct" }));
      focusWord(word);
      return true;
    },
    [layoutBundle, checkWordCompletion, focusWord]
  );

  const handleHint = useCallback(() => {
    if (!layoutBundle) return;

    if (!firstHintUsedRef.current) {
      const firstWord = getWordsByClueOrder()[0];
      if (!firstWord || completedRef.current.has(firstWord.answer)) return;
      firstHintUsedRef.current = true;
      setActiveWordKey(
        `${firstWord.starty},${firstWord.startx},${firstWord.orientation}`
      );
      setCurrentDirection(firstWord.orientation);
      revealLetterInWord(firstWord, 0);
      return;
    }

    if (!activeWordKey) return;

    const word = layoutBundle.words.find(
      (w) => `${w.starty},${w.startx},${w.orientation}` === activeWordKey
    );
    if (!word || completedRef.current.has(word.answer)) return;

    for (let i = 0; i < word.answer.length; i++) {
      const r = word.orientation === "down" ? word.starty - 1 + i : word.starty - 1;
      const c = word.orientation === "across" ? word.startx - 1 + i : word.startx - 1;
      const key = `${r},${c}`;
      if (!isCrosswordCellFilled(cellValuesRef.current[key])) {
        revealLetterInWord(word, i);
        return;
      }
    }
  }, [layoutBundle, activeWordKey, getWordsByClueOrder, revealLetterInWord]);

  const handleSubmit = useCallback(
    async (auto = false, skip = false) => {
      if (submitting || submittedRef.current) return;

      submittedRef.current = true;
      setSubmitting(true);

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;
      const timeStr = getTimeString();
      const score = skip ? 0 : points;

      if (!isDemoBypass && backendBase && adminToken && stageRowIdRef.current) {
        await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId: stageRowIdRef.current,
          stageNumber: 1,
          score,
          time: timeStr,
          total_score: score,
          reportIdRef,
        });
      }

      sessionStorage.removeItem(STORAGE_TIME_KEY);
      clearStageActive(1);
      markStageCompleted(1, storedUser);

      await showStageCompleteCelebration({
        stageNumber: 1,
        skip,
        score: skip ? 0 : points,
      });

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      navigate("/stage2");
    },
    [
      submitting,
      layoutBundle,
      points,
      getTimeString,
      isDemoBypass,
      backendBase,
      adminToken,
      storedUser,
      navigate,
    ]
  );

  const handleSkip = useCallback(() => {
    handleSubmit(false, true);
  }, [handleSubmit]);

  useEffect(() => {
    if (!layoutBundle?.words.length || submittedRef.current) return;
    const allDone = layoutBundle.words.every((w) =>
      completedAnswers.has(w.answer)
    );
    if (allDone) {
      handleSubmit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-finish once all words done
  }, [completedAnswers, layoutBundle?.words]);

  const desktopGridStyle = useMemo(() => {
    if (!layoutBundle) return { gap: GRID_GAP };
    const { cols, rows } = layoutBundle.layout;
    return {
      gap: GRID_GAP,
      gridTemplateColumns: `repeat(${cols}, ${DESKTOP_CELL}px)`,
      gridTemplateRows: `repeat(${rows}, ${DESKTOP_CELL}px)`,
      width: `${DESKTOP_CELL * cols + (cols - 1) * GRID_GAP}px`,
      fontSize: `${DESKTOP_CELL * 0.5}px`,
    };
  }, [layoutBundle]);

  const [mobileGridStyle, setMobileGridStyle] = useState(null);

  const fitGrid = useCallback(() => {
    if (!layoutBundle) return;
    const { cols, rows } = layoutBundle.layout;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    if (!isMobile) {
      setMobileGridStyle(null);
      return;
    }

    const grid = gridRef.current;
    const wrap = grid?.parentElement;
    const available = wrap
      ? Math.floor(wrap.getBoundingClientRect().width - 12)
      : window.innerWidth - 24;
    let cellSize = Math.floor((available - (cols - 1) * GRID_GAP) / cols);
    while (cellSize > 10 && cellSize * cols + (cols - 1) * GRID_GAP > available - 2) {
      cellSize -= 1;
    }
    if (cellSize < 10) return;

    setMobileGridStyle({
      gap: GRID_GAP,
      gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
      gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
      width: `${cellSize * cols + (cols - 1) * GRID_GAP}px`,
      fontSize: `${Math.max(9, Math.round(cellSize * 0.46))}px`,
    });
  }, [layoutBundle]);

  useLayoutEffect(() => {
    setMobileGridStyle(null);
    fitGrid();
  }, [fitGrid, layoutBundle]);

  useEffect(() => {
    window.addEventListener("resize", fitGrid);
    window.addEventListener("orientationchange", fitGrid);

    const wrap = gridRef.current?.parentElement;
    let resizeObserver;
    if (wrap && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => fitGrid());
      resizeObserver.observe(wrap);
    }

    return () => {
      window.removeEventListener("resize", fitGrid);
      window.removeEventListener("orientationchange", fitGrid);
      resizeObserver?.disconnect();
    };
  }, [fitGrid, layoutBundle]);

  const isCellHighlighted = useCallback(
    (row, col) => {
      if (!activeWordKey || !layoutBundle) return false;
      const word = layoutBundle.words.find(
        (w) => `${w.starty},${w.startx},${w.orientation}` === activeWordKey
      );
      if (!word) return false;
      const row0 = word.starty - 1;
      const col0 = word.startx - 1;
      for (let i = 0; i < word.answer.length; i++) {
        const r = word.orientation === "down" ? row0 + i : row0;
        const c = word.orientation === "across" ? col0 + i : col0;
        if (r === row && c === col) return true;
      }
      return false;
    },
    [activeWordKey, layoutBundle]
  );

  const renderClue = (word) => {
    const posKey = `${word.starty},${word.startx}`;
    const num = wordStartPositions[posKey] ?? word.position;
    const done = completedAnswers.has(word.answer);
    const clueKey = `${word.starty},${word.startx},${word.orientation}`;
    const active = activeWordKey === clueKey;
    return (
      <p
        key={clueKey}
        className={`crossword-stage__clue${active ? " crossword-stage__clue--active" : ""}${
          done ? " crossword-stage__clue--done" : ""
        }`}
        style={themedTextStyle}
        onClick={() => focusWord(word)}
      >
        <strong>{num}.</strong> {word.clue}
        {done && (
          <>
            {" "}
            :{" "}
            <span className="crossword-stage__clue-answer">
              {word.answer.charAt(0).toUpperCase() + word.answer.slice(1)}
            </span>
          </>
        )}
      </p>
    );
  };

  const showHelp = () => {
    fireStageHelpSwal({
      rules: getStageRulesFromTheme(themeData, "downandacross_rules"),
      themeData,
    });
  };

  if (themeStatus === "loading" || themeStatus === "idle" || !replayChecked) {
    return <div className="crossword-stage__loading">Loading game…</div>;
  }

  if (!layoutBundle || !layoutBundle.words.length) {
    return (
      <div className="crossword-stage__empty">
        No crossword questions configured. Add Down and Across pairs in theme admin.
      </div>
    );
  }

  const { layout } = layoutBundle;
  const { table, rows, cols } = layout;
  const gridStyle = mobileGridStyle ?? desktopGridStyle;
  const correctPopupImg = themeData?.puzzle_image;

  return (
    <div className="crossword-stage">
      <header className="crossword-stage__header">
        <div className="crossword-stage__title-block">
          <span className="crossword-stage__badge">STAGE 1</span>
          <h1 className="crossword-stage__title" style={themedTextStyle}>
            {title}
          </h1>
          <p className="crossword-stage__subtitle" style={themedTextStyle}>
            {subtitle}
          </p>
        </div>
        <div className="crossword-stage__hud">
          <div className="crossword-stage__stats-card">
            <div className="crossword-stage__stat">
              <span aria-hidden>⏱</span>
              <span>{timerDisplay}</span>
            </div>
            <div className="crossword-stage__stat">
              <span aria-hidden>★</span>
              <span>{points}</span>
            </div>
          </div>
          <div className="stage-hud-actions">
            <button
              type="button"
              className="crossword-stage__help-btn"
              aria-label="How to play"
              onClick={showHelp}
            >
              ?
            </button>
            <button
              type="button"
              className="game-stage-actions__skip-btn game-stage-actions__skip-btn--hud"
              disabled={submitting}
              onClick={handleSkip}
            >
              Skip
            </button>
          </div>
        </div>
      </header>

      <div className="crossword-stage__body">
        <aside className="crossword-stage__clues">
          <div className="crossword-stage__clues-row">
            <div className="crossword-stage__clue-card crossword-stage__clue-card--across">
              <h3 style={themedTextStyle}>ACROSS</h3>
              {acrossClues.map(renderClue)}
            </div>
            <div className="crossword-stage__clue-card crossword-stage__clue-card--down">
              <h3 style={themedTextStyle}>DOWN</h3>
              {downClues.map(renderClue)}
            </div>
          </div>
          <div className="crossword-stage__actions">
            <button type="button" className="crossword-stage__hint-btn" onClick={handleHint}>
              <span aria-hidden>💡</span> Hint
            </button>
            <button
              type="button"
              className="crossword-stage__submit-btn"
              disabled={submitting}
              onClick={() => handleSubmit(false, false)}
            >
              SUBMIT
            </button>
          </div>
        </aside>

        <div className="crossword-stage__grid-col">
          <div className="crossword-stage__grid-wrap">
            <div
              ref={gridRef}
              className="crossword-stage__grid"
              style={gridStyle}
            >
              {Array.from({ length: rows }, (_, i) =>
                Array.from({ length: cols }, (_, j) => {
                  if (table[i][j] === "-") {
                    return (
                      <div
                        key={`${i}-${j}`}
                        className="crossword-stage__cell crossword-stage__cell--block"
                      />
                    );
                  }
                  const key = `${i},${j}`;
                  const posKey = `${i + 1},${j + 1}`;
                  const num = wordStartPositions[posKey];
                  const dir = getDirection(i, j, table);
                  const state = cellStates[key];
                  const highlight = isCellHighlighted(i, j);
                  const correct = table[i][j].toUpperCase();
                  const locked = state === "correct";

                  return (
                    <div
                      key={key}
                      className={`crossword-stage__cell${
                        highlight ? " crossword-stage__cell--highlight" : ""
                      }${state === "correct" ? " crossword-stage__cell--correct" : ""}${
                        state === "wrong" ? " crossword-stage__cell--wrong" : ""
                      }`}
                      style={
                        window.matchMedia("(max-width: 768px)").matches
                          ? undefined
                          : { width: DESKTOP_CELL, height: DESKTOP_CELL }
                      }
                    >
                      {num != null && (
                        <span className="crossword-stage__cell-num">{num}</span>
                      )}
                      <input
                        className="crossword-stage__input"
                        type="text"
                        maxLength={1}
                        data-cw-row={i}
                        data-cw-col={j}
                        value={cellValues[key] || ""}
                        disabled={locked}
                        onFocus={() => {
                          if (isAutoNavigatingRef.current) {
                            isAutoNavigatingRef.current = false;
                            return;
                          }

                          const preferOrientation =
                            currentDirectionRef.current || dir;
                          const w = findWordAtCell(
                            layoutBundle.words,
                            i,
                            j,
                            preferOrientation
                          );
                          if (w) {
                            const wordKey = `${w.starty},${w.startx},${w.orientation}`;
                            setActiveWordKey(wordKey);
                            activeWordKeyRef.current = wordKey;
                            setCurrentDirection(w.orientation);
                            currentDirectionRef.current = w.orientation;
                          }
                        }}
                        onChange={(e) =>
                          handleCellInput(i, j, e.target.value, dir)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !(cellValues[key] || "")) {
                            focusSiblingInWord(i, j, -1, currentDirection || dir);
                          }
                        }}
                        aria-label={`Cell row ${i + 1} column ${j + 1}, correct letter ${correct}`}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      <StageCorrectPopup
        open={
          showCorrectPopup &&
          Boolean(correctPopupImg || themeData?.Correct_ans_text_popup)
        }
        imageUrl={
          correctPopupImg ? getDvSourceImageUrl(correctPopupImg) : ""
        }
        text={themeData?.Correct_ans_text_popup || ""}
      />

    </div>
  );
}
